import { useCallback, useEffect, useState } from 'react'
import { getAccruals } from '@/services/waterfall'
import { MEMBERS, TIER_LABELS } from '@/types/waterfall'
import { HoverHelp, TableToolbar } from '@/tableControls'
import { useMaxMd } from '@/hooks/useMaxMd'
import { formatApiError } from '@/utils/apiError'
import { formatDisplayDate } from '@/utils/waterfallCalculator'
import WaterfallTabHeader from './WaterfallTabHeader'
import CapitalAccounts from './CapitalAccounts'
import DistributionModeToggle from './DistributionModeToggle'
import { useDistributionModeStore } from '@/store/distributionModeStore'

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const TIERS = ['t1', 'hp', 's3a', 's3b'] as const

const MEM_COL_CLASS = 'w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem]'
const MOBILE_MEM_COL_CLASS = 'w-[4.25rem] min-w-[4.25rem] max-w-[4.25rem]'
const STICKY_TIER_HEAD_CLASS =
  'sticky left-0 z-30 min-w-[5.5rem] max-w-[8rem] bg-gray-50 border-r border-gray-200'
const STICKY_TIER_CELL_CLASS =
  'sticky left-0 z-20 min-w-[5.5rem] max-w-[8rem] bg-white border-r border-gray-200'
const STICKY_TIER_NET_CLASS =
  'sticky left-0 z-20 min-w-[5.5rem] max-w-[8rem] bg-blue-50 border-r border-gray-200'
const STICKY_NUM_HEAD_CLASS =
  'sticky left-0 top-0 z-30 w-10 min-w-[2.5rem] max-w-[2.5rem] bg-gray-50 border-r border-gray-200'
const STICKY_NUM_CELL_CLASS =
  'sticky left-0 z-20 w-10 min-w-[2.5rem] max-w-[2.5rem] bg-white border-r border-gray-200'
const STICKY_NUM_HIGHLIGHT_CLASS =
  'sticky left-0 z-20 w-10 min-w-[2.5rem] max-w-[2.5rem] bg-amber-50 border-r border-gray-200'
const STICKY_HEAD_CLASS = 'sticky top-0 z-20 bg-gray-50'

export function reasonsFor(
  member: (typeof MEMBERS)[number],
  accruals: Record<string, Record<string, number>>,
): string[] {
  const t1 = accruals.t1?.[member] ?? 0
  const hp = accruals.hp?.[member] ?? 0
  const s3a = accruals.s3a?.[member] ?? 0
  const s3b = accruals.s3b?.[member] ?? 0
  const s4b = accruals.s4b?.[member] ?? 0
  const classB = s3b + s4b
  const net = accruals.net?.[member] ?? 0
  const vestingPayable = accruals.vesting_payable?.[member] ?? 0
  const vestingMembers = accruals.vesting_members as Record<string, boolean> | undefined
  const isVestingMember = vestingMembers?.[member] === true
  const reasons: string[] = []

  if (Math.abs(net) <= 0.005) return reasons

  if (t1 > 0.005) {
    reasons.push('Tier 1 catch-up from a prior distribution that skipped the Tier 1 stub')
  }
  if (hp > 0.005) {
    reasons.push('Tier 2 (HP) catch-up from a prior under-sized distribution')
  }
  if (s3a > 0.005) {
    reasons.push('Tier 3A catch-up from cumulative Class A mix')
  }
  if (classB > 0.005 && isVestingMember) {
    reasons.push(
      `Vested share withheld — accrued to Distributions Payable (${fmt.format(vestingPayable)} funded by payable; remainder is milestone step-up owed from new cash). Pay from the Calculator's Vesting catch-up panel.`,
    )
  } else {
    if (s3b > 0.005) {
      reasons.push('Tier 3B shortfall vs the vesting-basis owed row')
    }
    if (s3b < -0.005) {
      reasons.push('Tier 3B paid above the vesting-basis owed row')
    }
  }
  if (reasons.length === 0) {
    reasons.push('Outstanding from prior distributions')
  }
  return reasons
}

const memberAccrualReasons = reasonsFor

function escapeCsvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function csvMoney(value: number): string {
  return value.toFixed(2)
}

export function owedPaidCsv(
  rows: AccrualRow[],
  memberFilter: (typeof MEMBERS)[number] | 'all',
): string {
  const headers = [
    '#',
    'Date',
    'Owed (this dist)',
    'Paid (this dist)',
    'Withheld (this dist)',
    'Catch-up (this dist)',
    'Cum owed',
    'Cum paid',
    'Outstanding',
  ]
  const csvRows = rows.map((row) => {
    const pick = (byMember?: MemberAmounts, total?: number) =>
      memberFilter === 'all' ? (total ?? sumMemberAmounts(byMember)) : (byMember?.[memberFilter] ?? 0)
    return [
      String(row.num),
      row.date,
      csvMoney(pick(row.owed_by_member, row.owed_total)),
      csvMoney(pick(row.paid_by_member, row.paid_total)),
      csvMoney(pick(row.withheld_by_member, sumMemberAmounts(row.withheld_by_member))),
      csvMoney(pick(row.catch_up_by_member, sumMemberAmounts(row.catch_up_by_member))),
      csvMoney(pick(row.cum_owed_by_member, row.cum_owed_total)),
      csvMoney(pick(row.cum_paid_by_member, row.cum_paid_total)),
      csvMoney(pick(row.outstanding_by_member, row.outstanding_total)),
    ]
      .map(escapeCsvCell)
      .join(',')
  })
  return [headers.join(','), ...csvRows].join('\n')
}

function downloadOwedPaidCsv(rows: AccrualRow[], memberFilter: (typeof MEMBERS)[number] | 'all') {
  const blob = new Blob([owedPaidCsv(rows, memberFilter)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  const suffix = memberFilter === 'all' ? 'all' : memberFilter
  anchor.download = `accruals-owed-vs-paid-${suffix}.csv`
  anchor.click()
  URL.revokeObjectURL(url)
}

function sumMemberAmounts(amounts?: Record<string, number>): number {
  return MEMBERS.reduce((sum, m) => sum + (amounts?.[m] ?? 0), 0)
}

type MemberAmounts = Record<string, number>

interface AccrualRow {
  num: number
  date: string
  owed_by_member?: MemberAmounts
  paid_by_member?: MemberAmounts
  cum_owed_by_member?: MemberAmounts
  cum_paid_by_member?: MemberAmounts
  outstanding_by_member?: MemberAmounts
  withheld_by_member?: MemberAmounts
  catch_up_by_member?: MemberAmounts
  owed_total: number
  paid_total: number
  cum_owed_total?: number
  cum_paid_total?: number
  outstanding_total?: number
}

function AccrualsCash() {
  const maxMd = useMaxMd()
  const memColClass = maxMd ? MOBILE_MEM_COL_CLASS : MEM_COL_CLASS
  const cellPadClass = maxMd ? 'px-2 py-2' : 'px-4 py-2'
  const historyCellPadClass = maxMd ? 'px-2 py-1.5' : 'px-3 py-1.5'
  const [accruals, setAccruals] = useState<Record<string, Record<string, number>> | null>(null)
  const [rows, setRows] = useState<AccrualRow[]>([])
  const [apiNotes, setApiNotes] = useState<string[]>([])
  const [memberFilter, setMemberFilter] = useState<(typeof MEMBERS)[number] | 'all'>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getAccruals()
      setAccruals(data.accruals)
      setRows(data.rows as unknown as AccrualRow[])
      setApiNotes(data.notes ?? [])
    } catch (e) {
      setError(formatApiError(e, 'Failed to load accruals'))
      setAccruals(null)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const latestDate = rows.length > 0 ? rows[rows.length - 1]?.date : null

  const description = latestDate
    ? `Per-member owed and overpaid balances by tier through distribution #${rows[rows.length - 1]?.num} (${formatDisplayDate(latestDate)}).`
    : 'Per-member owed and overpaid balances by tier through the latest logged distribution.'

  if (loading) {
    return <p className="text-sm text-gray-500">Loading accruals…</p>
  }

  if (error) {
    return (
      <div>
        <WaterfallTabHeader title="Accruals" description={description} />
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      </div>
    )
  }

  const net = accruals?.net ?? {}

  return (
    <div className="w-full space-y-6">
      <WaterfallTabHeader title="Accruals" description={description} />

      {latestDate && (
        <p className="text-sm text-gray-600">
          Latest logged accrual as of{' '}
          <span className="font-medium text-gray-900">{formatDisplayDate(latestDate)}</span>
        </p>
      )}

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
        <p className="font-medium text-gray-900">How these get repaid</p>
        <ul className="mt-2 list-disc space-y-1.5 pl-5">
          <li>
            <span className="font-medium">Tier 1 and Tier 2</span> balances fold into the next
            calculated distribution on the Calculator (with Apply true-ups on). Expand a tier row
            to see the catch-up lines — including the 2× HP match on back-paid Tier 1, which is
            separate from the current-period stub.
          </li>
          <li>
            Cents-level Tier 1/2 lines are cumulative-day-count rounding and fold into the next
            distribution.
          </li>
          <li>
            Scott&apos;s Tier 3B/4B line is his withheld vested share plus any milestone step-up not
            yet paid — it equals the Vesting catch-up panel&apos;s payable from prior distributions
            and is paid there, never through an ordinary distribution.
          </li>
        </ul>
        {apiNotes.length > 0 && (
          <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-600">
            {apiNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {MEMBERS.map((m) => {
          const value = net[m] ?? 0
          const owed = value > 0.005
          const reasons = accruals ? memberAccrualReasons(m, accruals) : []
          return (
            <div
              key={m}
              className={`rounded-lg border p-4 shadow-sm ${
                owed ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'
              }`}
            >
              <p className="text-xs text-gray-500">{m}</p>
              <p className={`mt-1 text-lg font-semibold tabular-nums ${owed ? 'text-amber-900' : 'text-gray-900'}`}>
                {fmt.format(value)}
              </p>
              <p className="mt-0.5 text-xs text-gray-500">
                {owed ? 'Owed to partner' : value < -0.005 ? 'Overpaid' : 'Current'}
              </p>
              {reasons.length > 0 && (
                <ul className="mt-1.5 space-y-0.5 text-xs leading-snug text-gray-600">
                  {reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>

      {accruals && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="container-title">Tier accruals by member</h3>
          </div>
          <div className="table-scroll-mobile overflow-x-auto overscroll-x-contain">
            <table className="min-w-full border-separate border-spacing-0 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className={`${STICKY_TIER_HEAD_CLASS} ${cellPadClass} text-left font-medium text-gray-600`}
                  >
                    Tier
                  </th>
                  {MEMBERS.map((m) => (
                    <th
                      key={m}
                      className={`${memColClass} ${STICKY_HEAD_CLASS} ${cellPadClass} text-right font-medium text-gray-600`}
                    >
                      {m}
                    </th>
                  ))}
                  <th
                    className={`${memColClass} ${STICKY_HEAD_CLASS} ${cellPadClass} text-right font-medium text-gray-600 border-l border-gray-300`}
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {TIERS.map((tier) => (
                  <tr key={tier}>
                    <td className={`${STICKY_TIER_CELL_CLASS} ${cellPadClass} text-gray-700`}>
                      {TIER_LABELS[tier]}
                    </td>
                    {MEMBERS.map((m) => (
                      <td key={m} className={`${memColClass} ${cellPadClass} text-right tabular-nums`}>
                        {fmt.format(accruals[tier]?.[m] ?? 0)}
                      </td>
                    ))}
                    <td
                      className={`${memColClass} ${cellPadClass} text-right tabular-nums font-medium text-gray-900 border-l border-gray-300`}
                    >
                      {fmt.format(sumMemberAmounts(accruals[tier]))}
                    </td>
                  </tr>
                ))}
                <tr aria-label="Net tier accruals by member">
                  <td
                    className={`${STICKY_TIER_NET_CLASS} ${cellPadClass} border-t-2 border-t-blue-200 font-bold text-gray-900`}
                  >
                    Net
                  </td>
                  {MEMBERS.map((m) => (
                    <td
                      key={m}
                      className={`${memColClass} ${cellPadClass} text-right tabular-nums bg-blue-50 border-t-2 border-t-blue-200 font-bold text-gray-900`}
                    >
                      {fmt.format(net[m] ?? 0)}
                    </td>
                  ))}
                  <td
                    className={`${memColClass} ${cellPadClass} text-right tabular-nums bg-blue-50 border-t-2 border-t-blue-200 font-bold text-gray-900 border-l border-gray-300`}
                  >
                    {fmt.format(sumMemberAmounts(net))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="container-title">Owed vs paid by distribution</h3>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-gray-600">
                Member
                <select
                  value={memberFilter}
                  onChange={(e) =>
                    setMemberFilter(e.target.value as (typeof MEMBERS)[number] | 'all')
                  }
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900"
                >
                  <option value="all">All members (total)</option>
                  {MEMBERS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <TableToolbar
                onExport={() => downloadOwedPaidCsv(rows, memberFilter)}
                exportDisabled={rows.length === 0}
              />
            </div>
          </div>
          <div className="table-scroll-mobile max-h-96 overflow-x-auto overflow-y-auto overscroll-x-contain">
            <table className="min-w-full border-separate border-spacing-0 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`${STICKY_NUM_HEAD_CLASS} ${historyCellPadClass} text-left`}>#</th>
                  <th className={`${STICKY_HEAD_CLASS} ${historyCellPadClass} text-left whitespace-nowrap`}>
                    Date
                  </th>
                  <th className={`${STICKY_HEAD_CLASS} ${historyCellPadClass} text-right`}>
                    <HoverHelp
                      label="Owed (this dist)"
                      aria-label="Owed (this dist) help"
                    >
                      <p>The incremental amount owed for that event.</p>
                    </HoverHelp>
                  </th>
                  <th className={`${STICKY_HEAD_CLASS} ${historyCellPadClass} text-right`}>
                    <HoverHelp label="Paid (this dist)" aria-label="Paid (this dist) help">
                      <p>
                        Cash actually paid; withheld vested shares are excluded and catch-ups
                        included.
                      </p>
                    </HoverHelp>
                  </th>
                  <th className={`${STICKY_HEAD_CLASS} ${historyCellPadClass} text-right`}>
                    Withheld (this dist)
                  </th>
                  <th className={`${STICKY_HEAD_CLASS} ${historyCellPadClass} text-right`}>
                    Catch-up (this dist)
                  </th>
                  <th className={`${STICKY_HEAD_CLASS} ${historyCellPadClass} text-right`}>Cum owed</th>
                  <th className={`${STICKY_HEAD_CLASS} ${historyCellPadClass} text-right`}>Cum paid</th>
                  <th className={`${STICKY_HEAD_CLASS} ${historyCellPadClass} text-right font-medium`}>
                    <HoverHelp label="Outstanding" aria-label="Outstanding help">
                      <p>
                        The running cumulative owed minus paid; matches the summary cards above.
                      </p>
                    </HoverHelp>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => {
                  const owed =
                    memberFilter === 'all'
                      ? row.owed_total
                      : (row.owed_by_member?.[memberFilter] ?? 0)
                  const paid =
                    memberFilter === 'all'
                      ? row.paid_total
                      : (row.paid_by_member?.[memberFilter] ?? 0)
                  const cumOwed =
                    memberFilter === 'all'
                      ? (row.cum_owed_total ?? 0)
                      : (row.cum_owed_by_member?.[memberFilter] ?? 0)
                  const cumPaid =
                    memberFilter === 'all'
                      ? (row.cum_paid_total ?? 0)
                      : (row.cum_paid_by_member?.[memberFilter] ?? 0)
                  const withheld =
                    memberFilter === 'all'
                      ? sumMemberAmounts(row.withheld_by_member)
                      : (row.withheld_by_member?.[memberFilter] ?? 0)
                  const catchUp =
                    memberFilter === 'all'
                      ? sumMemberAmounts(row.catch_up_by_member)
                      : (row.catch_up_by_member?.[memberFilter] ?? 0)
                  const outstanding =
                    memberFilter === 'all'
                      ? (row.outstanding_total ?? 0)
                      : (row.outstanding_by_member?.[memberFilter] ?? 0)
                  const isLast = row.num === rows[rows.length - 1]?.num
                  const stickyNumClass = isLast ? STICKY_NUM_HIGHLIGHT_CLASS : STICKY_NUM_CELL_CLASS
                  return (
                    <tr key={row.num} className={isLast ? 'bg-amber-50/40' : undefined}>
                      <td className={`${stickyNumClass} ${historyCellPadClass}`}>{row.num}</td>
                      <td className={`${historyCellPadClass} whitespace-nowrap`}>{row.date}</td>
                      <td className={`${historyCellPadClass} text-right tabular-nums`}>
                        {fmt.format(owed)}
                      </td>
                      <td className={`${historyCellPadClass} text-right tabular-nums`}>
                        {fmt.format(paid)}
                      </td>
                      <td className={`${historyCellPadClass} text-right tabular-nums text-gray-600`}>
                        {fmt.format(withheld)}
                      </td>
                      <td className={`${historyCellPadClass} text-right tabular-nums text-gray-600`}>
                        {fmt.format(catchUp)}
                      </td>
                      <td className={`${historyCellPadClass} text-right tabular-nums text-gray-600`}>
                        {fmt.format(cumOwed)}
                      </td>
                      <td className={`${historyCellPadClass} text-right tabular-nums text-gray-600`}>
                        {fmt.format(cumPaid)}
                      </td>
                      <td
                        className={`${historyCellPadClass} text-right tabular-nums font-medium ${
                          Math.abs(outstanding) > 0.005 ? 'text-amber-900' : 'text-gray-700'
                        }`}
                      >
                        {fmt.format(outstanding)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Accruals() {
  const mode = useDistributionModeStore((s) => s.mode)
  return (
    <div className="flex flex-col items-start">
      <DistributionModeToggle className="mb-6" />
      <div className="w-full">
        {mode === 'income' ? <CapitalAccounts /> : <AccrualsCash />}
      </div>
    </div>
  )
}
